// SPDX-License-Identifier: GPL-3.0-or-later
// The feedback core of the game page: ONE bus, ONE settings store and ONE flash limiter for the
// whole page. The rules, the engine bridge and the input observer emit into the bus. The effects,
// the sound, the popups and the touch haptics read it. The page makes the core once and gives the
// same objects to every reader.
//
// Time, storage, `matchMedia` and the connection probe come in as arguments, so a test runs the
// core with plain objects.

import type { InputEvent, RenderFrame, TableSpec } from "../src/shared/contracts.ts";
import {
  type ConnectionLike,
  createFeedbackBus,
  createFlashLimiter,
  createSettingsStore,
  type FeedbackBus,
  type FeedbackSink,
  type FlashLimiter,
  type MatchMediaLike,
  type SettingsStore,
  type StorageLike,
} from "../src/shared/feedback/index.ts";
import {
  createInputFeedback,
  type InputObserver,
  type PositionOf,
  positionLookup,
} from "../src/table/feedback/index.ts";
import {
  DEFAULT_PARK_POSITION,
  DEFAULT_PLUNGER_STROKE,
  type PlungerInfo,
  powerFraction,
  pullFraction,
} from "./power.ts";

/** What the core takes from the platform. */
export interface FeedbackPlatform {
  /** Local storage, or null when the browser blocks it. */
  readonly storage: StorageLike | null;
  /** `window.matchMedia`, or null. */
  readonly matchMedia: MatchMediaLike | null;
  /** `navigator.connection`. It may give nothing. */
  readonly connection: () => ConnectionLike | null | undefined;
  /** A clock in ms. It must not go back. */
  readonly now: () => number;
}

/** The core. */
export interface FeedbackCore {
  readonly bus: FeedbackBus;
  readonly store: SettingsStore;
  readonly limiter: FlashLimiter;
  readonly positionOf: PositionOf;
  /** Call it with each input that the page sends to the engine. It never sends one. */
  readonly observeInput: InputObserver;
  /**
   * Call it with each physics frame. While the plunger is held it emits `plunger_charge` with the
   * pull, so the charge tone follows the pull.
   */
  chargeFrame(frame: RenderFrame): void;
}

/** The parts of a window that `platformOf` reads. */
export interface PlatformWindow {
  readonly localStorage: StorageLike;
  readonly matchMedia?: (query: string) => { readonly matches: boolean };
  readonly navigator: unknown;
  readonly performance: { now(): number };
}

/** Local storage, or null when the browser blocks the access. */
function localStorageOf(win: PlatformWindow): StorageLike | null {
  try {
    return win.localStorage;
  } catch {
    return null;
  }
}

/** The platform of a window. */
export function platformOf(win: PlatformWindow): FeedbackPlatform {
  const media = win.matchMedia;
  return {
    storage: localStorageOf(win),
    matchMedia: typeof media === "function" ? (query) => media.call(win, query) : null,
    connection: () => (win.navigator as { connection?: ConnectionLike }).connection,
    now: () => win.performance.now(),
  };
}

/**
 * The charge emitter. It watches the plunger button and emits one event per physics frame while the
 * button is down. It reuses one event object: a reader must not keep it.
 */
function createChargeEmitter(
  sink: FeedbackSink,
  plunger: PlungerInfo,
): {
  readonly observe: InputObserver;
  readonly frame: (frame: RenderFrame) => void;
} {
  const stroke = plunger.stroke ?? DEFAULT_PLUNGER_STROKE;
  const park = plunger.parkPosition ?? DEFAULT_PARK_POSITION;
  const event = { kind: "plunger_charge" as const, strength: 0 };
  let held = false;
  let pull = park;
  return {
    observe(ev) {
      if (ev.action === "plunger") held = ev.pressed;
    },
    frame(frame) {
      if (!held) return;
      pull = pullFraction(frame.items, plunger.name, stroke, pull);
      event.strength = powerFraction(pull, park);
      sink.emit(event);
    },
  };
}

/** Make the core for a table. */
export function createFeedbackCore(table: TableSpec, platform: FeedbackPlatform): FeedbackCore {
  const bus = createFeedbackBus();
  const store = createSettingsStore({
    storage: platform.storage,
    matchMedia: platform.matchMedia,
    connection: platform.connection,
  });
  const limiter = createFlashLimiter({ policy: () => store.get(), now: platform.now });
  const positionOf = positionLookup(table);
  const inputs = createInputFeedback(bus, positionOf);
  const charge = createChargeEmitter(bus, table.plunger);
  return {
    bus,
    store,
    limiter,
    positionOf,
    observeInput(ev: InputEvent) {
      inputs(ev);
      charge.observe(ev);
    },
    chargeFrame: charge.frame,
  };
}
